import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Select,
  MenuItem,
  ListSubheader,
  Box,
  alpha,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import AddIcon from "@mui/icons-material/Add";
import { TextFieldForViewOnly } from "./TextField/TextField";
import { JustInfo } from "./Icon";

const CREATE_OPTION_VALUE = "__grouped_select_create__";

const CustomMenuItemRoot = styled(MenuItem)(({ theme }) => ({
  position: "relative",
  paddingLeft: theme.spacing(3),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius, // rounded corners

  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      width: 3,
      height: "100%",
      backgroundColor: theme.palette.primary.main,
    },
  },

  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

export const StyledMenuItem = ({ children, ...props }) => (
  <CustomMenuItemRoot {...props}>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 500,
        letterSpacing: 0.2,
        fontSize: "0.9rem",
        color: "text.primary",
      }}
    >
      {children}
    </Typography>
  </CustomMenuItemRoot>
);

const GroupedSelect = ({
  value,
  onChange,
  options,
  sx = {},
  menuProps = {},
  fullWidth = true,
  size = "small",
  disabled = false,
  placeholder = "Select an option",
  selectedItemStyle = {},
  groupHeaderStyle = {},
  enableSearch = false,
  searchPlaceholder = "Search options...",
  canEdit = true,
  onCreateOption,
  createOptionLabel,
  onDropdownOpen,
  onDropdownClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuItemRefs = useRef([]);
  const createOptionRef = useRef(null);
  const trimmedSearch = searchTerm.trim();
  const canCreateOption = typeof onCreateOption === "function";

  // Filter and group options based on search term
  const groupedOptions = useMemo(() => {
    let filteredOptions = options;

    if (enableSearch && trimmedSearch) {
      const searchLower = trimmedSearch.toLowerCase();
      filteredOptions = options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchLower) ||
          String(option.value).toLowerCase().includes(searchLower) ||
          String(option.group || "")
            .toLowerCase()
            .includes(searchLower)
      );
    }

    return filteredOptions.reduce((acc, option) => {
      const group = option.group || "";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(option);
      return acc;
    }, {});
  }, [options, trimmedSearch, enableSearch]);

  const visibleOptions = useMemo(
    () => Object.values(groupedOptions).flat(),
    [groupedOptions]
  );

  const visibleOptionIndexMap = useMemo(() => {
    const map = new Map();
    visibleOptions.forEach((option, index) => {
      map.set(option.value, index);
    });
    return map;
  }, [visibleOptions]);

  useEffect(() => {
    menuItemRefs.current = menuItemRefs.current.slice(0, visibleOptions.length);
  }, [visibleOptions.length]);

  const hasExactMatch = useMemo(() => {
    if (!trimmedSearch) return false;
    const searchLower = trimmedSearch.toLowerCase();
    return options.some(
      (option) => String(option.label || "").trim().toLowerCase() === searchLower
    );
  }, [options, trimmedSearch]);

  const showCreateOption =
    canCreateOption && enableSearch && trimmedSearch && !hasExactMatch;

  const resolvedCreateLabel =
    typeof createOptionLabel === "function"
      ? createOptionLabel(trimmedSearch)
      : createOptionLabel || `Add "${trimmedSearch}"`;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleMenuOpen = () => {
    setSearchTerm(""); // Reset search when menu opens
    setMenuOpen(true);
    onDropdownOpen?.();
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setSearchTerm("");
    onDropdownClose?.();
  };

  const handleChange = (event) => {
    if (event.target.value === CREATE_OPTION_VALUE) {
      if (showCreateOption) {
        onCreateOption(trimmedSearch);
        handleMenuClose();
      }
      return;
    }
    onChange?.(event);
  };

  const handleCreateFromSearch = () => {
    if (!showCreateOption) return;
    onCreateOption(trimmedSearch);
    handleMenuClose();
  };

  const focusMenuOptionFromSearch = (direction) => {
    const focusableItems = menuItemRefs.current.filter(Boolean);
    if (showCreateOption && createOptionRef.current) {
      focusableItems.push(createOptionRef.current);
    }

    const target =
      direction === "up"
        ? focusableItems[focusableItems.length - 1]
        : focusableItems[0];

    if (!target) return;

    requestAnimationFrame(() => {
      target.focus();
      target.scrollIntoView?.({ block: "nearest" });
    });
  };

  if (!canEdit) {
    return <TextFieldForViewOnly value={value || ""} />;
  }

  return (
    <Select
      value={value || ""}
      open={menuOpen}
      onOpen={handleMenuOpen}
      onClose={handleMenuClose}
      onChange={handleChange}
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
      displayEmpty
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[8],
            border: (theme) => `1px solid ${theme.palette.divider}`,
            "& .MuiListSubheader-root": {
              position: "sticky",
              top: enableSearch ? 64 : 0,
              zIndex: 2,
              backgroundColor: (theme) =>
                alpha(theme.palette.background.paper, 0.98),
              backdropFilter: "blur(12px)",
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              lineHeight: 1.5,
              ...groupHeaderStyle,
            },
          },
          ...menuProps.PaperProps,
        },
        ...menuProps,
      }}
      sx={{
        backgroundColor: "background.paper",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: (theme) =>
            `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
        "&.Mui-focused": {
          boxShadow: (theme) =>
            `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
        "& .MuiSelect-select": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minHeight: "20px",
          display: "flex",
          alignItems: "center",
          "&:focus": {
            backgroundColor: "transparent",
          },
        },
        "& .MuiSelect-icon": {
          right: "12px",
          color: "text.secondary",
          fontSize: "1.25rem",
          transition: "transform 0.2s ease-in-out",
        },
        "& .MuiSelect-iconOpen": {
          transform: "rotate(180deg)",
        },
        "&.Mui-disabled": {
          backgroundColor: "background.default",
        },
        ...sx,
      }}
    >
      {enableSearch && (
        <Box
          sx={{
            p: 2,
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TextField
            size='small'
            placeholder={searchPlaceholder || "Search options..."}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                focusMenuOptionFromSearch(
                  e.key === "ArrowUp" ? "up" : "down"
                );
                return;
              }

              if (e.key === "Enter" && showCreateOption) {
                e.preventDefault();
                handleCreateFromSearch();
              }
            }}
            fullWidth
            variant='outlined'
            autoComplete='off'
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon
                    color='action'
                    fontSize='small'
                    sx={{ color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      handleSearchChange({ target: { value: "" } })
                    }
                    edge='end'
                    sx={{ mr: -1 }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                fontSize: "0.875rem",
                borderRadius: 1,
                backgroundColor: (theme) => alpha(theme.palette.grey[50], 0.5),
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                  boxShadow: (theme) =>
                    `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              },
            }}
          />
        </Box>
      )}

      {!value && (
        <MenuItem
          value=''
          disabled
          sx={{
            color: "text.secondary",
            fontStyle: "italic",
            fontSize: "0.875rem",
          }}
        >
          <Box sx={{ color: "text.secondary" }}>{placeholder}</Box>
        </MenuItem>
      )}

      {Object.entries(groupedOptions).map(([group, groupOptions]) => [
        <ListSubheader
          key={group}
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: "primary.dark",
            py: 1.5,
            px: 2,
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            lineHeight: 1.5,
            borderBottom: (theme) =>
              `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            "&:first-of-type": {
              mt: 0,
            },
            ...groupHeaderStyle,
          }}
        >
          {group}
        </ListSubheader>,
        ...groupOptions.map((option) => {
          const optionIndex = visibleOptionIndexMap.get(option.value);

          return (
            <MenuItem
              key={option.value}
              ref={(node) => {
                if (optionIndex !== undefined) {
                  menuItemRefs.current[optionIndex] = node;
                }
              }}
              value={option.value}
              selected={value === option.value}
              sx={{
                pl: 3,
                py: 1,
                "&.Mui-selected": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.12),
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.18),
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    width: 3,
                    height: "100%",
                    backgroundColor: "primary.main",
                  },
                  ...selectedItemStyle,
                },
                "&:hover": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.08),
                },
                "&.Mui-focusVisible, &:focus": {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  color: (theme) =>
                    value === option.value
                      ? theme.palette.primary.main
                      : "inherit",
                  minWidth: 0,
                }}
              >
                <Box
                  component='span'
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.label}
                </Box>
                {option.infoTitle && (
                  <Box
                    component='span'
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    sx={{ display: "inline-flex", flexShrink: 0 }}
                  >
                    <JustInfo title={option.infoTitle} ml={0.5} mr={0} />
                  </Box>
                )}
              </Box>
            </MenuItem>
          );
        }),
      ])}

      {enableSearch &&
        trimmedSearch &&
        Object.keys(groupedOptions).length === 0 &&
        !showCreateOption && (
          <MenuItem disabled>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3,
                color: "text.secondary",
                fontSize: "0.875rem",
                width: "100%",
              }}
            >
              <SearchOffIcon
                sx={{
                  fontSize: "2rem",
                  mb: 1,
                  color: "text.disabled",
                }}
              />
              <Box sx={{ fontWeight: 500 }}>No results found</Box>
              <Box sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                Try adjusting your search: "{searchTerm}"
              </Box>
            </Box>
          </MenuItem>
        )}

      {canCreateOption && enableSearch && !trimmedSearch && (
        <MenuItem disabled>
          <Box
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              py: 0.5,
              width: "100%",
            }}
          >
            Type a name to add a new option
          </Box>
        </MenuItem>
      )}

      {showCreateOption && (
        <MenuItem
          ref={createOptionRef}
          value={CREATE_OPTION_VALUE}
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            color: "primary.main",
            fontWeight: 600,
            py: 1.25,
            position: "sticky",
            bottom: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.08),
            },
            "&.Mui-focusVisible, &:focus": {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <AddIcon fontSize='small' />
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {resolvedCreateLabel}
            </Box>
          </Box>
        </MenuItem>
      )}
    </Select>
  );
};

export default GroupedSelect;
